import { useState, useCallback, useRef } from 'react';
import {
  getConversationMessages,
  sendMessage as sendMessageService,
  SEND_ASYNCHRONOUS_AXIOS_TIMEOUT_MS,
} from '../services/conversations.js';
import { useChatApiClient } from '../context/ChatApiClientContext.jsx';
import { detectContentFormat, resolveMessageSourceContent } from '../utils/contentUtils.js';

/**
 * Hook that handles messages for a specific conversation.
 * Calls the service getConversationMessages() directly; keeps local state only (no global store).
 * Same pattern as useHomeChatConversations: shared services + local module state.
 */
export const useHomeChatMessages = () => {
  const apiClient = useChatApiClient();
  const [messages, setMessages] = useState([]);
  const [agentIsTyping, setAgentIsTyping] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  const isSendingRef = useRef(false);

  /**
   * Fetches messages for a specific conversation from the service.
   * Ignores responses for older requests when switching conversations quickly.
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Array>} Mapped messages list
   */
  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      setError(null);
      return [];
    }
    const requestId = ++fetchIdRef.current;
    try {
      setIsLoadingMessages(true);
      setError(null);
      setMessages([]);

      const response = await getConversationMessages(apiClient, { conversationId });
      if (requestId !== fetchIdRef.current) return [];

      // API returns { statusCode, body: [...], meta } — body is the messages array
      const list = Array.isArray(response.body)
        ? response.body
        : Array.isArray(response.messages)
          ? response.messages
          : Array.isArray(response.data)
            ? response.data
            : [];

      const mapped = list
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || a.lastUpdated || a.timestamp || 0).getTime();
          const dateB = new Date(b.createdAt || b.lastUpdated || b.timestamp || 0).getTime();
          return dateA - dateB; // Oldest first for chat messages
        })
        .map((msg) => {
          // API may return text in multiple fields (see HistoryAgentStore)
          const content =
            msg.formattedText ??
            msg.messageText ??
            msg.message ??
            msg.content ??
            msg.text ??
            msg.body ??
            '';
          const sourceContent = resolveMessageSourceContent(msg);
          const contentFormat = detectContentFormat(sourceContent);
          // Normalize role: API uses senderRole "USER"|"AGENT" or direction "INBOUND"|"OUTBOUND"
          const senderRole = (msg.senderRole || msg.role || '').toUpperCase();
          const direction = (msg.direction || '').toUpperCase();
          const isUser =
            senderRole === 'USER' ||
            direction === 'INBOUND' ||
            msg.role === 'user' ||
            msg.sender === 'user';
          const role = isUser ? 'user' : msg.role || msg.sender || 'assistant';

          return {
            id: msg.messageUuid || msg.id || msg.uuid,
            uuid: msg.messageUuid || msg.id || msg.uuid,
            messageUuid: msg.messageUuid || msg.id || msg.uuid,
            content: typeof content === 'string' ? content : String(content || ''),
            sourceContent,
            contentFormat,
            role,
            createdAt: msg.createdAt || msg.timestamp,
            timestamp: msg.createdAt || msg.timestamp,
            conversationId: conversationId,
            attachments: msg.attachments || [],
            metadata: msg.metadata || {},
            // Persisted processing steps (message-list TVF). Workflow runs
            // stamp a "workflow_run" marker step on every message they
            // persist — foldWorkflowRuns groups the run's messages into its
            // inline activity accordion from these.
            steps: Array.isArray(msg.steps) ? msg.steps : [],
          };
        });

      if (requestId !== fetchIdRef.current) return [];
      setMessages(mapped);
      return mapped;
    } catch (err) {
      if (requestId === fetchIdRef.current) {
        console.error('Error fetching conversation messages:', err);
        setError(err);
        setMessages([]);
      }
      return [];
    } finally {
      if (requestId === fetchIdRef.current) {
        setIsLoadingMessages(false);
      }
    }
  }, [apiClient]);

  const cleanThread = useCallback(() => {
    setMessages([]);
    setAgentIsTyping(false);
    setError(null);
  }, []);

  /**
   * Sends a message and refreshes the thread. Call with conversationId and agentId from context.
   * @param {string} messageText - Text to send
   * @param {Object} [options] - { conversationId, agentId, fileUuids, attachmentDetails, llmConfig, context }
   * @param {Array} [options.attachmentDetails] - For optimistic UI: [{ fileUuid, name, fileType, isLoading }]
   * @param {Object} [options.llmConfig] - Optional { llm_model, llm_model_provider } for model selection
   * @param {Object} [options.context] - Injected context (location, entity, entityValue, scope) — Story 7. scope dentro de context.
   */
  const sendMessage = useCallback(async (messageText, options = {}) => {
    const { conversationId, agentId, fileUuids, attachmentDetails, llmConfig, context } = options;
    const trimmed = typeof messageText === "string" ? messageText.trim() : "";
    if (!trimmed) return null;
    if (isSendingRef.current) return null;

    isSendingRef.current = true;
    setError(null);
    setAgentIsTyping(true);

    const hasDetails = Array.isArray(attachmentDetails) && attachmentDetails.length > 0;
    const fromExplicit = Array.isArray(fileUuids) ? fileUuids.filter(Boolean) : [];
    const fromDetails = hasDetails
      ? attachmentDetails
          .map((a) => a.fileUuid ?? a.document_uuid)
          .filter((uuid) => uuid != null && String(uuid).length > 0)
      : [];
    const effectiveFileUuids = fromExplicit.length > 0 ? fromExplicit : fromDetails;
    const hasFileUuids = effectiveFileUuids.length > 0;
    const optimisticAttachments = hasDetails
      ? attachmentDetails.map((a) => ({
          fileUuid: a.fileUuid ?? a.document_uuid,
          name: a.name ?? "Document",
          fileType: a.fileType ?? "",
          isLoading: a.isLoading === true,
        }))
      : hasFileUuids
        ? effectiveFileUuids.map((uuid) => ({
            fileUuid: uuid,
            name: 'Document',
            fileType: '',
            isLoading: false,
          }))
        : [];

    const optimisticId = `opt-${Date.now()}`;
    const optimisticCreatedAt = new Date().toISOString();
    const optimisticUserMessage = {
      id: optimisticId,
      uuid: optimisticId,
      messageUuid: optimisticId,
      content: trimmed,
      role: "user",
      createdAt: optimisticCreatedAt,
      timestamp: optimisticCreatedAt,
      conversationId: conversationId || null,
      attachments: optimisticAttachments,
      metadata: {},
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);

    try {
      const response = await sendMessageService(
        apiClient,
        {
          messageText: trimmed,
          conversationUuid: conversationId,
          agentUuid: agentId,
          channel: "WEB",
          fileUuids: hasFileUuids ? effectiveFileUuids : undefined,
          ...(llmConfig && typeof llmConfig === "object" && { llmConfig }),
          ...(context && typeof context === "object" && { context }),
        },
        { timeout: SEND_ASYNCHRONOUS_AXIOS_TIMEOUT_MS },
      );
      return response;
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err);
      return null;
    } finally {
      isSendingRef.current = false;
    }
  }, [apiClient]);

  return {
    messages,
    setMessages,
    agentIsTyping,
    setAgentIsTyping,
    isLoadingMessages,
    setIsLoadingMessages,
    error,
    setError,
    fetchMessages,
    cleanThread,
    sendMessage,
  };
};

export { useHomeChatMessages as useChatMessages };
